import { AdServerStroeerSponsorshipSettings } from '@mbrtargeting/metatag-config-types';
import { createHtmlFragment, createIntervalObservable } from '@mbrtargeting/metatag-utils';
import { Phase } from '../../../interfaces/constants.js';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onModuleRegistration } from '../../../loader/decorators/on-module-registration.js';
import { onPhase } from '../../../loader/decorators/on-phase.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectAdserverSponsorshipSettings, selectCommonSettings, selectModuleSettings } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { AdslotRegistry } from '../../../loader/modules/adslot-registry.js';
import { ConsentCache } from '../../../loader/modules/consent-cache.js';
import { ADSLOT_REGISTRY, CONFIG_RESOLVER, CONSENT_CACHE } from '../../../loader/token.js';
import { markSdg } from '../../../utils/performance.js';
import { sendCorsRequestPromise } from '../../../utils/send-cors-request.js';
import { simpleParamsSerializer } from '../../../utils/url-helper.js';
import { GLOBAL_TARGETING, STORAGE_HELPER } from '../../token.js';
import { LifecycleAction } from '../adslot-controller/adslot-lifecycle-state.js';
import { Adslot, isAdslotInstance } from '../adslot-controller/adslot.js';
import { CreativeSource, CreativeSourceLoadSlotsParams } from '../adslot-controller/creative-source.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';
import { StorageConsent, StorageHelper, StorageType } from '../storage/storage-helper.js';

const log = logger.logGroup({ prefix: 'StroeerSponsorship' });

@injectionTarget()
export class StroeerSponsorship implements CreativeSource {

    @inject(ADSLOT_REGISTRY) #adslotRegistry!: AdslotRegistry;
    @inject(CONFIG_RESOLVER) #configResolver!: ConfigResolver;
    @inject(CONSENT_CACHE) #consentCache!: ConsentCache;
    @inject(GLOBAL_TARGETING) #globalTargeting!: GlobalTargeting;
    @inject(STORAGE_HELPER) #storageHelper!: StorageHelper;

    /**
     * In-memory storage for campaigns.
     */
    #allStoredCampaigns: Record<string, Record<string, ScheduleItem[]>> = {};

    /**
     * Returns the configuration for the Stroeer Sponsorship module.
     *
     * @returns the configuration object
     */
    getConfig = (): AdServerStroeerSponsorshipSettings => this.#configResolver.get(selectAdserverSponsorshipSettings()) ?? {};

    getYlSiteId = (): string | undefined => this.#configResolver.get(selectModuleSettings('PREBID'))?.yieldloveDomainName;
    /**
     * Checks if we have consent or legitimate interest for
     *  - purpose 2 ("Use limited data to select advertising") and
     *  - purpose 7 ("Measure advertising performance")
     * or if GDPR does not apply.
     *
     * **NOTE**: purpose 1 is not included here as it is checked before reading or writing local storage
     *
     * @returns true if we are allowed to proceed, false otherwise
     */
    isAllowed = () => this.#consentCache.ifConsentConditions([
        { consentedPurposes: [2, 7], consentedVendors: [1057] },
        { legitimateInterestPurposes: [2, 7], legitimateInterestVendors: [1057] },
        { gdprApplies: false },
    ]);

    /**
     * Restores campaigns from local storage.
     */
    @onModuleRegistration()
    public restoreCampaigns(): void {
        // restore campaigns from local storage whenever we get consent for purpose 1 ("store and/or access information on a device")
        this.#storageHelper.get(StorageType.LOCAL, StorageConsent.WAIT, 'sdgCampaigns').then(campaignsSerialized => {
            this.#allStoredCampaigns = campaignsSerialized ? JSON.parse(campaignsSerialized) : {};
        });
    }

    /**
     * Delayed and periodic fetch of scheduled campaigns and stores them in memory and local storage.
     */
    @onPhase({ phase: Phase.TARGETING })
    public fetchCampaigns(): void {
        const { active, scheduleUrl, siteId } = this.getConfig();
        const { kvFilter = [] } = this.#configResolver.get(selectCommonSettings()) || {};

        // fetch campaigns immediately and then every 5 minutes for single-page-applications
        createIntervalObservable({ interval: 5 * 6e4, immediate: true }).subscribe(() => {

            // do not run the network request if module is not active
            if (!active || !this.isAllowed()) return;

            const request: ScheduleRequest = {
                websiteId: siteId,
                kvg: this.#globalTargeting.filter(([key]) => !kvFilter.includes(key)).toObject(),
                positions: this.#adslotRegistry.getAdslots().filter(isAdslotInstance).map(slot => ({
                    name: slot.slotName,
                    adUnits: slot.getAdUnits(),
                    sizes: slot.getSizes(),
                    kvl: slot.getTargeting().filter(([key]) => !kvFilter.includes(key)).toObject(),
                })),
            };

            sendCorsRequestPromise<Record<string, ScheduleItem[]>>({
                src: scheduleUrl,
                method: 'POST',
                body: JSON.stringify(request),
            }).then(response => {
                this.#allStoredCampaigns[siteId] = response;
                return this.#storageHelper.set(StorageType.LOCAL, StorageConsent.WAIT, 'sdgCampaigns', JSON.stringify(this.#allStoredCampaigns));
            })
        });
    }

    public async loadSlots({ adslots, done }: CreativeSourceLoadSlotsParams): Promise<void> {
        const { active, creativeUrl, siteId } = this.getConfig();
        const isDemo = this.#globalTargeting.has('demo');
        const isAllowed = this.isAllowed();

        // if module is not active or a demo campaign was requested, or we have no consent, skip the adslots
        if (!active || isDemo || !isAllowed) {
            log.debug('skipping slots %o (active=%s, isDemo=%s, isAllowed=%s)', [adslots.map(s => s.slotName).join(','), active, isDemo, isAllowed]);
            done(adslots);
            return;
        }

        // Remove milliseconds from the date to match the server response
        const now = `${new Date().toISOString().slice(0, 19)}Z`;

        for (const adSlot of adslots) {
            const match = this.findQualifyingCampaign(adSlot, now);

            if (!match) {
                // no matching campaign found, goto to next adslot
                continue;
            }

            if (match.blocker) {
                log.info(`Injecting sponsorship blocker for position ${adSlot.slotName}`);
                markSdg(`sponsorship_blocker_injected_${adSlot.slotName}`);
                await adSlot.lifecycle.dispatch(LifecycleAction.BLOCK_HIDE);

                // Adslot is blocked, goto next Adslot
                continue;
            }

            log.info(`Injecting sponsorship creative for position ${adSlot.slotName}`);
            markSdg(`sponsorship_creative_injected_${adSlot.slotName}`);

            // Valid ad found, make sure adslot is not auctioned off by other creative source. Await confirmation.
            await adSlot.lifecycle.dispatch(LifecycleAction.FILL);

            // Slot will start rendering and measure performance of rendering process but not await its result
            adSlot.renderCreative(sendCorsRequestPromise<string>({
                src: this.buildCreativeUrl(creativeUrl, adSlot, match.creativeId, siteId),
                method: 'GET',
                responseType: 'text'
            }).then(html => {
                const targetNode = adSlot.getStableDivElement({
                    id: `sdgHyperRenderingNode-${adSlot.slotName}`,
                    emptyOnReload: true,
                });

                // create an iframe via createHtmlFragment to render the ad code and attach it to the targetNode
                const iframe = targetNode.appendChild(createHtmlFragment({
                    html,
                    styles: {
                        display: 'inline',
                        width: match.width ? `${match.width}px` : 'inherit',
                        height: match.height ? `${match.height}px` : 'inherit',
                    }
                }));

                // attach DV script inside stable-element & measure advert viewability.
                adSlot.addVisibilityNode({ name: 'hyperRender', node: iframe });
            }))
        }

        done(adslots);
    }

    private findQualifyingCampaign(adslot: Adslot, time: string): ScheduleItem | undefined {
        const { siteId } = this.getConfig();
        const campaignsForSite = this.#allStoredCampaigns[siteId] ?? {};

        return campaignsForSite[adslot.slotName]?.find(campaign => {
            return (time >= campaign.start) && (time < campaign.end);
        });
    }


    private buildCreativeUrl(creativeUrl: string, adSlot: Adslot, creativeId: string, siteId: string): string {
        const { gdprApplies = false, tcString = ''} = this.#consentCache.getTcData() ?? {} // This operation is safe as we have already checked if GDPR should be applied on startup
        const ylSiteId = this.getYlSiteId();

        const params: Record<string, string | number | boolean> = {
            crid: creativeId,
            wid: siteId,
            pos: adSlot.slotName,
            pt: adSlot.getPageType(),
            zo1: adSlot.getZone(),
            gdpr: gdprApplies,
            gdpr_consent: tcString
        };

        adSlot.getAdUnits().forEach((adUnit, index) => {
            params[`au${index + 1}`] = adUnit;
        });

        if (ylSiteId) params.ylsid = ylSiteId;

        return `${creativeUrl}?${simpleParamsSerializer(params)}`;
    }
}

// Having this here is temporary
interface ScheduleItem {
    creativeId: string;
    start: string;
    end: string;
    blocker: boolean;
    width: number;
    height: number;
}

interface ScheduleRequest {
    websiteId: string;
    kvg: Record<string, string[]>;
    positions: Position[];
}

interface Position {
    name: string;
    adUnits: string[];
    sizes: (number[] | 'fluid')[];
    kvl: Record<string, string[]>;
}
